import { useEffect, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toggleLanguage } from '@/i18n';
import {
  CalendarDays,
  CreditCard,
  ExternalLink,
  Globe,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Receipt,
  Scissors,
  Settings,
  Shield,
  Sun,
  UserRoundCheck,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import type { Permission } from '@salon/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { applySalonTheme } from '@/lib/theme';
import { getSocket, disconnectSocket } from '@/lib/socket';
import { useAuth, useCan } from '@/stores/auth';
import { useUi } from '@/stores/ui';
import { useActiveBranch } from '@/api/hooks';
import { Avatar } from '@/components/ui/misc';
import { Select } from '@/components/ui/input';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  perm: Permission;
}

export function AppShell() {
  const { t } = useTranslation();
  const session = useAuth((s) => s.session)!;
  const setSession = useAuth((s) => s.setSession);
  const can = useCan();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { dark, toggleDark, sidebarOpen, setSidebar } = useUi();
  const { branch, branches, setBranch } = useActiveBranch();
  const salon = session.salon;

  // Dark mode is an admin-dashboard preference.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    return () => document.documentElement.classList.remove('dark');
  }, [dark]);

  useEffect(() => {
    if (salon) applySalonTheme(salon);
  }, [salon]);

  useEffect(() => setSidebar(false), [location.pathname, setSidebar]);

  // Live updates: another receptionist's change refreshes this screen.
  useEffect(() => {
    if (!salon) return;
    const socket = getSocket();
    const onAppt = () => {
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['appointment'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['walkins'] });
    };
    const onWalkin = () => void qc.invalidateQueries({ queryKey: ['walkins'] });
    const onInvoice = () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['invoice'] });
    };
    socket.on('appointment:changed', onAppt);
    socket.on('walkin:changed', onWalkin);
    socket.on('invoice:changed', onInvoice);
    return () => {
      socket.off('appointment:changed', onAppt);
      socket.off('walkin:changed', onWalkin);
      socket.off('invoice:changed', onInvoice);
    };
  }, [salon, qc]);

  const nav: NavItem[] = [
    { to: '/app/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, perm: 'report:read' as Permission },
    { to: '/app/calendar', label: t('nav.calendar'), icon: CalendarDays, perm: 'appointment:read' as Permission },
    { to: '/app/walkins', label: t('nav.walkins'), icon: UserRoundCheck, perm: 'walkin:manage' as Permission },
    { to: '/app/clients', label: t('nav.clients'), icon: Users, perm: 'client:read' as Permission },
    { to: '/app/pos', label: t('nav.pos'), icon: CreditCard, perm: 'pos:use' as Permission },
    { to: '/app/invoices', label: t('nav.invoices'), icon: Receipt, perm: 'invoice:read' as Permission },
    { to: '/app/services', label: t('nav.services'), icon: Scissors, perm: 'service:write' as Permission },
    { to: '/app/staff', label: t('nav.staff'), icon: UsersRound, perm: 'staff:read' as Permission },
    { to: '/app/settings', label: t('nav.settings'), icon: Settings, perm: 'template:manage' as Permission },
    { to: '/app/platform', label: t('nav.platform'), icon: Shield, perm: 'platform:admin' as Permission },
  ].filter((n) => can(n.perm));

  async function signOut() {
    await api.post('/auth/logout').catch(() => undefined);
    disconnectSocket();
    qc.clear();
    setSession(null);
    navigate('/login');
  }

  const sidebar = (
    <div className="flex h-full flex-col gap-2 p-4">
      <div className="flex items-center gap-3 px-2 py-2">
        {salon?.logoUrl ? (
          <img src={salon.logoUrl} alt="" className="h-9 w-9 rounded-xl object-cover" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary font-heading text-lg text-primary-foreground">
            {(salon?.name ?? 'S')[0]}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-heading text-base font-semibold">{salon?.name ?? t('app.name')}</p>
          <p className="truncate text-xs text-muted-foreground">{t(`roles.${session.user.role}`, { defaultValue: session.user.role.toLowerCase() })}</p>
        </div>
      </div>
      <div className="gold-divider my-2" />
      <nav className="flex flex-1 flex-col gap-1" aria-label="Main">
        {nav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      {salon && (
        <a
          href={`/book/${salon.slug}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
        >
          <ExternalLink className="h-4 w-4" /> {t('nav.bookingPage')}
        </a>
      )}
      <div className="gold-divider my-2" />
      <div className="flex items-center gap-3 px-2">
        <Avatar name={session.user.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{session.user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{session.user.email}</p>
        </div>
        <button onClick={signOut} className="rounded-full p-2 text-muted-foreground hover:bg-muted" aria-label={t('nav.signOut')} title={t('nav.signOut')}>
          <LogOut className="h-4 w-4 rtl:rotate-180" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="sticky top-0 hidden h-screen border-e bg-card lg:block">{sidebar}</aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setSidebar(false)} />
          <aside className="absolute inset-y-0 start-0 w-72 bg-card shadow-xl animate-in slide-in-from-left rtl:slide-in-from-right">
            <button className="absolute end-3 top-3 rounded-full p-2 hover:bg-muted" onClick={() => setSidebar(false)} aria-label="Close menu">
              <X className="h-4 w-4" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/85 px-4 backdrop-blur sm:px-6">
          <button className="rounded-full p-2 hover:bg-muted lg:hidden" onClick={() => setSidebar(true)} aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          {branches.length > 0 && (
            <div className="w-44 sm:w-56">
              <label htmlFor="branch-switch" className="sr-only">
                {t('common.branch')}
              </label>
              <Select id="branch-switch" className="h-9 rounded-full" value={branch?.id ?? ''} onChange={(e) => setBranch(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="ms-auto flex items-center gap-1">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm hover:bg-muted"
              aria-label="Switch language"
            >
              <Globe className="h-4 w-4" />
              <span>{t('common.language')}</span>
            </button>
            <button onClick={toggleDark} className="rounded-full p-2 hover:bg-muted" aria-label={t('common.theme')} aria-pressed={dark}>
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
